import React, { useMemo } from "react";
import TMTableHeader from "./TMTableHeader";
import TMLineItem from "./TMLineItem";
import { CATEGORIES } from "./constants";
import { calculateTotalCost } from "./helpers";

/**
 * TMTable Component
 * Displays Time & Materials line items in a table format
 * Supports Labor, Material, Equipment, and Subcontractors
 * Items are grouped by category with section headers showing item counts
 * @param {Array<Object>} [items=[]] Line items.
 * @param {Function} [onAddItems] Unused here; forwarded to {@link TMLineItem}.
 * @param {Function} [onChange] Called with the next items array.
 * @param {*} [loadedFromReport] Forwarded to {@link TMLineItem}.
 * @param {boolean} [showPricing=false] Show pricing columns (Price/Unit, Total) and total cost footer.
 * @param {boolean} [readOnly=false] Disables all interactions and hides action buttons.
 * @returns {JSX.Element}
 */
const TMTable = ({
  items = [],
  onAddItems,
  onChange,
  loadedFromReport,
  showPricing = false,
  readOnly = false,
}) => {
  // Group items by category
  const groupedItems = useMemo(() => {
    const groups = {};

    // Initialize all categories
    Object.keys(CATEGORIES).forEach((categoryKey) => {
      groups[categoryKey] = [];
    });

    // Group items by category
    items.forEach((item, index) => {
      // Find which category this item belongs to
      const categoryKey = Object.keys(CATEGORIES).find((key) =>
        CATEGORIES[key].types.includes(item.type)
      );

      if (categoryKey) {
        groups[categoryKey].push({ ...item, originalIndex: index });
      }
    });

    return groups;
  }, [items]);

  // Footer total uses the same line-item math as each row and the price form.
  const totalCost = useMemo(() => calculateTotalCost(items), [items]);

  /**
   * Replace the item at `index` and notify the parent.
   *
   * @param {number} index Item index in `items`.
   * @param {Object} updatedItem Next item value.
   * @returns {void}
   */
  const handleItemChange = (index, updatedItem) => {
    const newItems = [...items];
    newItems[index] = updatedItem;
    onChange?.(newItems);
  };

  /**
   * Insert a copy of the item at `index + 1` with a new `Date.now()` id.
   *
   * @param {number} index Source item index.
   * @returns {void}
   */
  const handleDuplicate = (index) => {
    const itemToDuplicate = items[index];
    const newItem = { ...itemToDuplicate, id: Date.now() }; // Simple ID generation
    const newItems = [...items];
    newItems.splice(index + 1, 0, newItem);
    onChange?.(newItems);
  };

  /**
   * Remove the item at `index`.
   *
   * @param {number} index Item index to remove.
   * @returns {void}
   */
  const handleRemove = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange?.(newItems);
  };

  return (
    <div className="border border-gray-300 rounded-md bg-white">
      <div className="">
        <table className="min-w-full min-[840px]:min-w-full divide-y divide-gray-200">
          <TMTableHeader showPricing={showPricing} readOnly={readOnly} />
          <tbody className="bg-white">
            {!items || items.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    showPricing ? (readOnly ? "8" : "9") : readOnly ? "5" : "6"
                  }
                  className="px-6 py-8 text-center text-gray-500"
                >
                  <i>No items added.</i>
                </td>
              </tr>
            ) : (
              Object.keys(CATEGORIES).map((categoryKey) => {
                const category = CATEGORIES[categoryKey];
                const categoryItems = groupedItems[categoryKey];
                const Icon = category.icon;

                // Skip categories with no items
                if (categoryItems.length === 0) return null;

                return (
                  <React.Fragment key={categoryKey}>
                    {/* Category Header Row */}
                    <tr
                      className={`${category.headerBgColor} border-t border-b border-gray-300`}
                    >
                      <td
                        colSpan={
                          showPricing
                            ? readOnly
                              ? "8"
                              : "9"
                            : readOnly
                              ? "5"
                              : "6"
                        }
                        className="px-2 py-1"
                      >
                        <div className="flex items-center gap-2">
                          <Icon className="w-5 h-5 text-gray-700" />
                          <span className="font-semibold text-sm text-gray-800">
                            {category.label}
                          </span>
                          <span className="text-xs text-gray-600">
                            ({categoryItems.length}{" "}
                            {categoryItems.length === 1 ? "item" : "items"})
                          </span>
                        </div>
                      </td>
                    </tr>

                    {/* Category Items */}
                    {categoryItems.map((item) => (
                      <TMLineItem
                        key={item.id || item.originalIndex}
                        item={item}
                        onAddItems={onAddItems}
                        onChange={(updatedItem) =>
                          handleItemChange(item.originalIndex, updatedItem)
                        }
                        onDuplicate={() => handleDuplicate(item.originalIndex)}
                        onRemove={() => handleRemove(item.originalIndex)}
                        showPricing={showPricing}
                        readOnly={readOnly}
                        loadedFromReport={loadedFromReport}
                      />
                    ))}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
          {/* Total Cost Footer - Only show when pricing is enabled */}
          {items && items.length > 0 && showPricing && (
            <tfoot className="bg-gray-50">
              <tr>
                <td className="pl-2 pr-1 py-2 text-sm text-gray-900 font-semibold">
                  Total Cost
                </td>
                <td
                  colSpan={readOnly ? 6 : 7}
                  className="px-[2px] md:px-2 text-sm text-center md:text-right text-gray-700"
                ></td>
                <td className="px-[2px] md:px-2 text-sm text-center md:text-right font-bold text-base text-gray-900">
                  $
                  {totalCost.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default TMTable;
